import { useRef, useState } from "react";
import { forgetPassword } from "../services/auth";
import { getApiErrorMessage } from "../utils/Util";
import { useLanguage } from "../context/LanguageContext";

function ForgotPasswordPopup() {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  const handleSubmit = async () => {
    if (!email || loading) return;
    setError("");
    setLoading(true);
    try {
      await forgetPassword(email);
      setSuccess(true);
      setTimeout(() => closeRef.current?.click(), 2500);
    } catch (err) {
      setError(getApiErrorMessage(err, t("forgotPassword.error")));
    } finally {
      setLoading(false);
    }
  };

  const handleShow = () => {
    setEmail("");
    setError("");
    setSuccess(false);
  };

  return (
    <div
      className="modal fade"
      id="forgotPasswordModal"
      tabIndex={-1}
      aria-hidden="true"
      ref={(el) => {
        if (!el) return;
        el.addEventListener("show.bs.modal", handleShow);
      }}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "380px" }}>
        <div className="modal-content rounded-4 shadow">
          <button
            ref={closeRef}
            type="button"
            className="btn-close position-absolute top-0 end-0 m-3"
            data-bs-dismiss="modal"
            aria-label={t("common.close")}
            style={{ zIndex: 1 }}
          />

          <div className="modal-body px-4 py-4 text-center">
            <div className="mb-2">
              <i className="bi bi-key-fill" style={{ fontSize: "3rem", color: "#c20e1a" }}></i>
            </div>

            <h6 className="fw-bold fs-5 mb-1">{t("forgotPassword.title")}</h6>
            <p className="text-muted small mb-4">{t("forgotPassword.subtitle")}</p>

            {success ? (
              <div className="text-center py-2">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: "2.5rem" }}></i>
                <p className="mt-2 fw-semibold">{t("forgotPassword.successMessage")}</p>
                <p className="text-muted small">{t("common.windowClosing")}</p>
              </div>
            ) : (
              <>
                <div className="mb-3 text-start">
                  <label className="form-label fw-semibold">{t("login.email")}</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-envelope"></i>
                    </span>
                    <input
                      type="email"
                      className="form-control"
                      placeholder={t("login.emailPlaceholder")}
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                      onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger py-2 small mb-3">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    {error}
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-danger w-100 rounded-pill py-2 fw-semibold"
                  onClick={handleSubmit}
                  disabled={!email || loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" />
                  ) : (
                    <i className="bi bi-send me-2"></i>
                  )}
                  {t("forgotPassword.button")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPopup;
